import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import 'chart.js/auto';
import { Chart } from '@totalsoft/rocket-ui';
import { getChartColors } from 'features/dashboard/utils';
import { useTheme, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';

const DashboardLineChart = ({ indicator }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const labels = useMemo(() => {
    const monthNumbers = Array.from(Array(12).keys());
    return monthNumbers.map(month => {
      const date = new Date();
      date.setMonth(month);
      return t('DATE_FORMAT', { date: { value: date, format: 'MMMM' } });
    });
  }, [t]);

  const datasets = useMemo(() => {
    const datasetLabels = [...new Set(indicator.graphic.map(item => item.year))];
    const colors = getChartColors(theme.palette.primary.main, datasetLabels.length);
    return datasetLabels.map((datasetLabel, dlIndex) => ({
      label: datasetLabel,
      data: labels.map((label, lIndex) => {
        const indicatorItem = indicator.graphic.find(item => item.month === lIndex && item.year === datasetLabel);
        return indicatorItem?.value || null;
      }),
      backgroundColor: colors[dlIndex],
      borderColor: alpha(colors[dlIndex], 0.6),
      lineTension: 0.5
    }));
  }, [indicator, theme, labels]);

  const data = useMemo(() => ({ labels, datasets }), [labels, datasets]);

  return (
    <Chart
      title={indicator.title}
      type='line'
      data={data}
      cardProps={{ sx: { height: '100%' } }}
      height={300}
      options={{
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              usePointStyle: true,
              padding: 15
            }
          },
          tooltip: {
            callbacks: {
              label: function () {
                return this.dataPoints[0]?.formattedValue;
              }
            }
          }
        }
      }}
    />
  );
};

DashboardLineChart.propTypes = {
  indicator: PropTypes.object.isRequired
};

export default DashboardLineChart;
